import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { ProgramService } from '../../../core/services/program.service';
import { ProgramActions } from './program.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getQueries } from './program.selectors';
import { ClientActions } from '../client/client.actions';
import { AlertService } from 'src/app/core/services/alert.service';

@Injectable()
export class ProgramEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly programService: ProgramService,
    private readonly store: Store,
    private readonly alertService: AlertService
  ) { }

  loadProgramsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.listPrograms),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(([, filters]) => {
        console.log(filters);

        return from(
          this.programService.listPrograms(
            filters.body ?? {},
            filters.params.page,
            filters.params.limit
          )
        ).pipe(
          map((programs) => {
            return ProgramActions.listProgramsSuccess({
              response: programs,
              filters,
            });
          }),
          catchError((error) =>
            of(ProgramActions.listProgramsFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadProgramNamesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.listProgramNames),
      mergeMap(() => {
        return from(this.programService.listProgramNames()).pipe(
          map((programs) => {
            return ProgramActions.listProgramNamesSuccess({
              response: programs,
            });
          }),
          catchError((error) =>
            of(ProgramActions.listProgramNamesFailure({ error: error.message }))
          )
        );
      })
    )
  );

  loadProgramNamesListByClientIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.listProgramNamesByClientIds),
      mergeMap((action) => {
        return from(
          this.programService.listProgramNamesByClientIds(
            action.client_ids.map((id) => id.toString())
          )
        ).pipe(
          map((programs) => {
            return ProgramActions.listProgramNamesByClientIdsSuccess({
              response: programs,
            });
          }),
          catchError((error) =>
            of(
              ProgramActions.listProgramNamesByClientIdsFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

  loadSingleProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.loadSingleProgram),
      mergeMap(({ id }) =>
        from(this.programService.getProgramById(id)).pipe(
          map((program) =>
            ProgramActions.loadSingleProgramSuccess({ program: program.data })
          ),
          catchError((error) =>
            of(
              ProgramActions.loadSingleProgramFailure({ error: error.message })
            )
          )
        )
      )
    )
  );

  createProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.createProgram),
      mergeMap((action) =>
        from(
          action.program.mode == 'ai'
            ? this.programService.createProgramAI(action.program)
            : this.programService.createProgramManual(action.program)
        ).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              `${action.program.program_name} `
            );
            return ProgramActions.createProgramSuccess({
              program: response.data,
              step: action.step,
            });
          }),
          catchError((error) => {
            return of(
              ProgramActions.createProgramFailure({ error: error.message })
            );
          })
        )
      )
    )
  );
  updateProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.updateProgram),
      mergeMap((action) =>
        from(this.programService.updateProgram(action.id, action.program)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              `${action.program.program_name} `
            );
            return ProgramActions.updateProgramSuccess({
              program: response.data,
              step: action.step,
            });
          }),
          catchError((error) =>
            of(ProgramActions.updateProgramFailure({ error: error.message }))
          )
        )
      )
    )
  );

  updateAiProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.updateAiProgram),
      mergeMap((action) =>
        from(
          this.programService.updateAiProgram(action.id, action.program)
        ).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              `${action.program.program_name} `
            );
            return ProgramActions.updateAiProgramSuccess({
              program: response.data,
              step: action.step,
            });
          }),
          catchError((error) =>
            of(ProgramActions.updateAiProgramFailure({ error: error.message }))
          )
        )
      )
    )
  );

  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ProgramActions.createProgramSuccess,
        ProgramActions.updateProgramSuccess,
        ProgramActions.updateAiProgramSuccess,
        ProgramActions.deleteProgramSuccess,
        ProgramActions.changeProgramStatusSuccess
      ),

      mergeMap((action) => {
        if (
          action.type === ProgramActions.createProgramSuccess.type ||
          action.type === ProgramActions.updateProgramSuccess.type ||
          action.type === ProgramActions.updateAiProgramSuccess.type
        ) {
          if (action.step !== 1 && action.step !== 5 && action.program._id) {
            return of(
              ProgramActions.loadSingleProgram({ id: action.program._id })
            );
          }
        }

        return of(
          ProgramActions.listPrograms(),
          ProgramActions.listProgramNames(),
          ClientActions.loadAllStatusClientNames()
        );
      })
    )
  );

  redirectAfterProgramChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProgramActions.createProgramSuccess,
          ProgramActions.updateProgramSuccess,
          ProgramActions.updateAiProgramSuccess
        ),
        tap((action) => {
          if (
            (action.step === 1 || action.step === 5) &&
            action.type !== ProgramActions.createProgramSuccess.type
          ) {
            this.router.navigate(['/programs'], {
              queryParamsHandling: 'preserve',
            });
          }
          if (
            (action.type === ProgramActions.createProgramSuccess.type ||
              action.type === ProgramActions.updateAiProgramSuccess.type) &&
            action.step !== 1 &&
            action.step !== 5
          ) {
            this.router.navigate(['/programs/edit/', action.program._id], {
              queryParams: { step: action.step },
              queryParamsHandling: 'merge', // keep other params (like id)
            });
          }
        }),
        mergeMap((action) => {
          if (action.step === 1 || action.step === 5) {
            return of(ProgramActions.clearSingleProgram());
          } else {
            return of(
              ProgramActions.loadSingleProgram({
                id: action.program._id,
              })
            );
          }
        })
      )
    // ✅ navigation side-effect only
  );

  redirectAfterCancelForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProgramActions.clearSingleProgram),
        tap(() => this.router.navigate(['/programs']))
      ),
    { dispatch: false } // ✅ navigation side-effect only
  );

  deleteProgram$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.deleteProgram),
      mergeMap((action) =>
        from(this.programService.deleteProgram(action.id)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              `${response.data.program_name}`
            );
            return ProgramActions.deleteProgramSuccess({ id: action.id });
          }),
          catchError((error) =>
            of(ProgramActions.deleteProgramFailure({ error: error.message }))
          )
        )
      )
    )
  );

  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.changeProgramStatus),
      mergeMap((action) =>
        from(this.programService.changeProgramStatus(action.id)).pipe(
          map((response) => {
            this.alertService.success(
              response.message,
              'Success',
              `${response.data.program_name}`
            );
            return ProgramActions.changeProgramStatusSuccess();
          }),
          catchError((error) =>
            of(
              ProgramActions.changeProgramStatusFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  //disposition module usecase
  getProgramNamesListByClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProgramActions.getProgramName_Client),
      mergeMap((action) => {
        return from(
          this.programService.getProgramName_Client(
            action.client_id 
          )
        ).pipe(
          map((programs) => {
            return ProgramActions.getProgramName_ClientSuccess({
              response: programs,
            });
          }),
          catchError((error) =>
            of(
              ProgramActions.getProgramName_ClientFailure({
                error: error.message,
              })
            )
          )
        );
      })
    )
  );

}
